import React, { useState, useEffect} from 'react'

//Node Modules
import { useTranslation, Trans } from 'react-i18next'
import {ListGroup, Modal} from 'react-bootstrap'

//Components
import Loading from 'components/Loading'
import { ExportIcon, CloseIcon, FilterIcon } from 'components/SASIcon'
import Search from 'components/Search'
import Result from './Result'
import Filters from './Filters'
import * as service from './service'

//Styles
import styles from './SearchModal.module.scss'

//Types
import { SearchResult, FilterOption } from 'types/search'
import { DocsetInfo as Docset } from 'types/docset'
import { CDCInfo as CDC } from 'types/cdc'

//Utils
import {isAdvSearch, collectionDocsets} from 'util/search'
import { HELP_DOCSET_ALIAS } from 'util/constants'

//Hooks
import { useIsSmallDevice } from 'hooks'

interface Props {
  id: string
  version: string
  collectionId?: string
  collectionVersion?: string
  currDocsetId?: string
  currDocsetVersion?: string
  currPage: string
  type: "docset" | "cdc" | "collection"
  docsets: Docset[]
  cdcs?: CDC[]
  onHide: () => void
  show: boolean,
  filterOptions: FilterOption[]
}

const SearchModal = ({id, version, collectionId, collectionVersion, currDocsetId, currDocsetVersion, currPage, type, docsets, cdcs, onHide, show, filterOptions}:Props) => {
  const isSmallDevice = useIsSmallDevice()
  const [query, setQuery] = useState<string>("")
  const [initial, setInitial] = useState<boolean>(true)
  const [resultCount, setResultCount] = useState<number>(0)
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [showFilters, setShowFilters] = useState<boolean>(!isSmallDevice)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>(filterOptions)
  const [externalLink, setExternalLink] = useState<string>('')

  const { t } = useTranslation()

  useEffect(()=>{
    if (isSmallDevice) {
      setShowFilters(false)
    } else {
      setShowFilters(true)
    }
  }, [isSmallDevice])

  useEffect(()=> {
    onSearch()
  }, [selectedFilters]) /* eslint-disable-line */

  const isActive = (result: SearchResult):boolean => {
    if( result.type === 'pdf') return false;
    if ( result.docset === HELP_DOCSET_ALIAS) return result.fileName === currPage
    if (type === 'docset') return result.docset === id && result.docsetVersion === version && result.fileName === currPage
    
    return result.docset === currDocsetId && result.docsetVersion === currDocsetVersion && result.fileName === currPage
  }

  const setNoResults = () => {
    setLoading(false)
    setResultCount(0)
    setResults([])
    setExternalLink('https://support.sas.com/en/search.html')
  }

   const escapeRegExp = (str: string):string => {
    return str.replace(/[\[\]\/\{\}\.\\\^\$\:\!]/g, "\\$&"); /* eslint-disable-line */

  }

  const onSearch = () => {
    if (query && query.trim()) {
      let searchQuery = query.replace(/  +/g, ' ');
      setSearchTerm(searchQuery.trim())
      let queryParams = new URLSearchParams()
      let encodedQuery = encodeURIComponent(escapeRegExp(searchQuery.trim()))
      queryParams.append('query', encodedQuery)
      queryParams.append('advancedSearch', isAdvSearch(searchQuery) ? 'true' : 'false')
      queryParams.append('type', type)

      if (type === 'cdc') {
        if (currDocsetId ) queryParams.append('docsetId', currDocsetId)
        if (currDocsetVersion) queryParams.append('docsetVersion', currDocsetVersion) 
        selectedFilters.forEach(item => {
          queryParams.append('docsets', item.value)
        })
        docsets.length === selectedFilters.length ? queryParams.append('includeHelp', 'true') : queryParams.append('includeHelp', 'false')
      }

      if (type === 'collection') {
        let selectedCdcs = cdcs!.filter( cdc => {
          return selectedFilters.findIndex( sf => {return sf.value === cdc.id}) > -1
        })

        let collDocsets = collectionDocsets(selectedCdcs)
        
        queryParams.append('includeHelp', 'true')
        if (currDocsetId ) queryParams.append('docsetId', currDocsetId)
        if (currDocsetVersion) queryParams.append('docsetVersion', currDocsetVersion) 
        if (collectionId) queryParams.append('collectionId', collectionId) 
        if (collectionVersion) queryParams.append('collectionVersion', collectionVersion)
        collDocsets.forEach(item => {
          if (item && item.id) queryParams.append('docsets', item.id)
        })
      }
      setInitial(false)
      setLoading(true)

      type === 'docset' ? service.docsetSearch(id, version, queryParams).then(({docs, numFound}) => {
        setResults(docs)
        setResultCount(numFound)
        setLoading(false)
      }, setNoResults).catch(setNoResults) 
      : 
      service.cdcSearch(id, version, queryParams).then(({docs, numFound}) => {
        setResults(docs)
        setResultCount(numFound)
        setLoading(false)
      }, setNoResults).catch(setNoResults)
    
      setExternalLink(`https://support.sas.com/en/search.html?q=${encodedQuery}%20site:documentation.sas.com`)
    } else {
      setSearchTerm('')
      setNoResults()
    }
  }

  const toggleFilters=()=>{
    setShowFilters(!showFilters)
  }

  const handleFilterSelect=(selectedOptions: FilterOption[])=> {
    if (selectedOptions.length === 0) {
      setSelectedFilters(filterOptions)
    } else {
      setSelectedFilters(selectedOptions)
    }
  }

  return (
    <>
      <Modal show={show} className={`${styles.modal} ${isSmallDevice ? styles.mobile : ''}`} scrollable onHide={onHide} backdropClassName={styles.backdrop}>
        <Modal.Header>
          { !isSmallDevice ? 
            <button type="button" className={`close`} onClick={onHide} aria-label={t("helpcenter-gui-icu.modal.close_button.aria")}>
              <CloseIcon />
            </button> : null
          }
          
          <div className={styles.search}>
            <Search onSubmit={onSearch} query={query} onChange={setQuery}/>
            {
              isSmallDevice ? 
              <div className={styles.actions}>
                <button type="button"  onClick={toggleFilters} aria-label={t('helpcenter-gui-icu.search.filters.button.label')} title={t('helpcenter-gui-icu.search.filters.button.label')}>
                  <FilterIcon />
                </button>
                <button type="button" onClick={onHide}>
                  <CloseIcon />
                </button>
              </div> :null
            }
          </div>
          { !initial && !loading &&             
            <div className={styles.summary}>
              <div className={styles.resultCount} tabIndex={0} role="alert">
                <Trans
                  t={t}
                  i18nKey="helpcenter-gui-icu.search.result_count.msg"
                  values={{value: resultCount}}
                />
                <i>{` ${searchTerm}`}</i>
              </div>
              <a target="_blank" href={externalLink} rel="noopener noreferrer" className={styles.externalSearch}>
                {t('helpcenter-gui-icu.search.support.link.label')}<ExportIcon />
              </a>
            </div>
          }
        </Modal.Header>
        { !initial && 
          <Modal.Body className={`${styles.body} ${isSmallDevice ? styles.smallDevice : ''}`}> 
            { loading && <Loading /> }
            { results && !loading &&
              <div className={styles.resultsContainer}>
                {!isSmallDevice || !showFilters ?
                  <div className={styles.results}>
                    { results.length > 0 ? 
                      <ListGroup variant="flush">
                        {
                          results.map(result => {
                            let locked = false;

                            if (result.secure === 'true') {
                              let docset = docsets.find( d => {return d.id === result.docset && d.version === result.docsetVersion}) as Docset
                              locked = docset ? docset.isLocked : true;
                            }
                            return (<Result key={result.id  || result.docset + result.docsetVersion + result.fileName} result={result} locked={locked} isActive={isActive(result)} onHide={onHide}/>)
                          })
                        }
                      </ListGroup> : <div className={styles.noResults}>{t('helpcenter-gui-icu.search.no_result.msg')}</div>
                    }
                  </div> : null
                }
                { type !== 'docset' && showFilters ?
                  <div className={styles.filters}>
                    <Filters options={filterOptions} selected={selectedFilters} setSelected={handleFilterSelect}/>
                  </div> : null
                }
            </div>
          
            }
          </Modal.Body>
        }
      </Modal>
    </>
  )
}

export default SearchModal